/* auth.js — JWT token persistence + axios helpers for the Member auth flow.
   Backend issues HttpOnly cookies, but we also receive `token` in the JSON
   response so cross-origin previews (where cookies may be blocked by browsers)
   still work via a Bearer fallback in localStorage. */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "slowcafe.auth.token";

export function getToken() {
    try { return localStorage.getItem(TOKEN_KEY) || null; }
    catch { return null; }
}

export function setToken(t) {
    try {
        if (t) localStorage.setItem(TOKEN_KEY, t);
        else localStorage.removeItem(TOKEN_KEY);
    } catch { /* ignore */ }
}

export function clearToken() { setToken(null); }

export function authHeaders() {
    const t = getToken();
    return t ? { Authorization: `Bearer ${t}` } : {};
}

export const authAxios = axios.create({
    baseURL: API,
    withCredentials: true,
});

authAxios.interceptors.request.use((config) => {
    const t = getToken();
    if (t) config.headers.Authorization = `Bearer ${t}`;
    return config;
});

// Friendly mapper for FastAPI 422 + plain string detail.
export function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    }
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export const PASSWORD_HINT = "Min 8 characters, with at least 1 letter and 1 digit.";

export function isStrongPassword(pw) {
    return typeof pw === "string"
        && pw.length >= 8
        && /[A-Za-z]/.test(pw)
        && /\d/.test(pw);
}

// ---- High-level API wrappers ----
export async function apiSetPasswordAfterCheckout({ session_id, setup_token, password, name }) {
    const payload = { password };
    if (session_id) payload.session_id = session_id;
    if (setup_token) payload.setup_token = setup_token;
    if (name) payload.name = name;
    const { data } = await authAxios.post("/auth/set-password", payload);
    if (data?.token) setToken(data.token);
    return data;
}

export async function apiLogin({ email, password }) {
    const { data } = await authAxios.post("/auth/login", { email, password });
    if (data?.token) setToken(data.token);
    return data;
}

export async function apiMe() {
    const { data } = await authAxios.get("/auth/me");
    return data;
}

export async function apiLogout() {
    try { await authAxios.post("/auth/logout"); } catch { /* ignore network errors */ }
    clearToken();
}

export async function apiForgotPassword({ email, origin_url }) {
    const { data } = await authAxios.post("/auth/forgot-password", { email, origin_url });
    return data;
}

export async function apiResetPassword({ token, password }) {
    const { data } = await authAxios.post("/auth/reset-password", { token, password });
    if (data?.token) setToken(data.token);
    return data;
}
